// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { Form, Space } from 'antd';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/TTP/TTPDrawer/extractData';
import { ICONS } from '@Constants/iconsApp';
import { FormatDate } from '@Molecules/Format';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import usePermissions from '@Hooks/usePermissions';
import { OrgPermissionsEnum, ProcedureOutput } from 'thehive-sdk';
import { useCurrentUser } from '@Queries/userQueries';
import { TTPEventList } from '@Organisms/TTP/eventList';

import './TTPDrawer.scss';

interface TTPDrawerProps {
    data: ProcedureOutput;
    eventList: TTPEventList;
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    scope?: 'case' | 'alert';
    visible: boolean;
    caseId?: string;
}

export const TTPDrawer = ({
    data,
    eventList,
    defaultVisible = false,
    onVisibleChange,
    scope = 'case',
    visible,
    caseId,
}: TTPDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const [form] = Form.useForm();
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const { _id: procedureId, _createdBy, _createdAt, _updatedAt } = data;
    const [fieldsTouched, setFieldsTouched] = useState(false);
    const hasPermissions =
        usePermissions({
            permissionsNeeded: [OrgPermissionsEnum.MANAGE_PROCEDURE],
            permissionsList: permissions,
        }) && scope === 'case';

    useEffect(() => {
        form.resetFields();
    }, [data, form]);

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.TTPIcon />
                    {i('procedure.drawer.title')}
                </Space>
            }
            className="drawer-form"
            width="50vw"
            disableClose={fieldsTouched}
            defaultVisible={defaultVisible}
            visible={visible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
        >
            <THForm<ProcedureOutput>
                name="ttp-drawer-form"
                form={form}
                editable
                initialValues={data}
                //eslint-disable-next-line
                onFinish={(data) => {
                    eventList.onEdit({ ...data }, procedureId);
                }}
            >
                <THForm.Layout
                    header={
                        <DisplayMetadata
                            items={[
                                {
                                    icon: <ICONS.SettingIcon />,
                                    label: 'id',
                                    value: <CopyValue value={procedureId}>{procedureId}</CopyValue>,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.created-by'),
                                    value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                                },
                                {
                                    icon: <ICONS.CreationDateIcon />,
                                    label: i('common.created-at'),
                                    value: <FormatDate date={_createdAt} />,
                                },
                                {
                                    icon: <ICONS.UpdateDateIcon />,
                                    label: i('common.updated-at'),
                                    value: <FormatDate date={_updatedAt} />,
                                    hidden: !_updatedAt,
                                },
                            ]}
                        />
                    }
                    footer={
                        <THForm.Item noStyle shouldUpdate>
                            {({ isFieldsTouched }) => (
                                <FormSubmitButton
                                    onCancel={() => form.resetFields()}
                                    disabled={false}
                                    footerText={i('general.confirm')}
                                    hidden={!isFieldsTouched()}
                                    formName="ttp-drawer-form"
                                />
                            )}
                        </THForm.Item>
                    }
                >
                    <Details
                        data={data}
                        extract={extractData({
                            locale: i,
                            hasPermissions,
                            caseId,
                        })}
                    />

                    <THForm.Item noStyle shouldUpdate>
                        {({ isFieldsTouched }) => setFieldsTouched(isFieldsTouched())}
                    </THForm.Item>
                </THForm.Layout>
            </THForm>
        </THDrawer>
    );
};
